import { Link } from 'react-router-dom';

import AgentRow from './agent-row';
import SimulationBanner from './simulation-banner';
import PageHeader from '../../../common/components/page-header/page-header';
import Section from '../../../common/components/section/section';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import Tag from '../../../common/components/tag/tag';
import { TEAM_ROLE_LABEL } from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { nameOf, useProjectCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useSeats } from '../../../global/contexts/coordination/coordination';
import { useSimulation } from '../contexts/simulation';
import { useTeam, useTeamMembers, useTeamProjects } from '../contexts/teams';
import { agentsOfTeam } from '../utils/agents';

import '../styles/detail.css';

export default function TeamDetail({ teamId }) {
    const teamQuery = useTeam(teamId);
    const membersQuery = useTeamMembers(teamId);
    const projectsQuery = useTeamProjects(teamId);
    const seatsQuery = useSeats();
    const { data: projects } = useProjectCatalog();
    const { overrides } = useSimulation();

    if (teamQuery.isPending) return <LoadingState label="Cargando equipo" rows={4} />;
    if (teamQuery.isError) {
        return (
            <>
                <PageHeader backTo={ROUTES.TEAMS} backLabel="Equipos" title="Equipo" />
                <ErrorState error={teamQuery.error} title="No pudimos abrir este equipo" onRetry={teamQuery.refetch} />
            </>
        );
    }

    const team = teamQuery.data;
    const members = membersQuery.data?.items ?? [];
    const agents = agentsOfTeam(seatsQuery.data?.items ?? [], members, overrides);
    const projectIds = (projectsQuery.data?.items ?? []).map((link) => link.projectId);

    return (
        <div className="team-detail">
            <PageHeader backTo={ROUTES.TEAMS} backLabel="Equipos" eyebrow="Equipo" title={team.name} description={team.description} />
            <SimulationBanner />

            <div className="team-detail__columns">
                <Section title="Agentes" count={seatsQuery.isSuccess ? agents.length : undefined}>
                    {(seatsQuery.isPending || membersQuery.isPending) && <LoadingState label="Cargando agentes" rows={3} />}
                    {seatsQuery.isError && <ErrorState error={seatsQuery.error} onRetry={seatsQuery.refetch} />}
                    {seatsQuery.isSuccess && membersQuery.isSuccess && agents.length === 0 && (
                        <EmptyState title="Sin agentes" description="Ningún integrante tiene puestos de agente activos." />
                    )}
                    {agents.length > 0 && (
                        <ul className="team-detail__agents">
                            {agents.map((agent) => <AgentRow key={agent.seat.id} agent={agent} isDetailed />)}
                        </ul>
                    )}
                </Section>

                <div className="team-detail__side">
                    <Section title="Proyectos" count={projectsQuery.isSuccess ? projectIds.length : undefined}>
                        {projectsQuery.isSuccess && projectIds.length === 0 && <EmptyState title="Sin proyectos asignados" />}
                        <ul className="team-detail__projects">
                            {projectIds.map((projectId) => (
                                <li key={projectId}>
                                    <Link to={ROUTES.PROJECT(projectId)} className="team-detail__project">{nameOf(projects, projectId, 'Proyecto')}</Link>
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section title="Integrantes" count={membersQuery.isSuccess ? members.length : undefined}>
                        {membersQuery.isError && <ErrorState error={membersQuery.error} onRetry={membersQuery.refetch} />}
                        <ul className="team-detail__members">
                            {members.map((member) => (
                                <li key={member.id} className="team-detail__member">
                                    <span>{member.label}</span>
                                    <Tag>{TEAM_ROLE_LABEL[member.role] ?? member.role}</Tag>
                                </li>
                            ))}
                        </ul>
                        <p className="team-detail__note">Las personas se muestran con etiquetas neutras en este borrador.</p>
                    </Section>
                </div>
            </div>
        </div>
    );
}
